import { useState, useEffect } from 'react'

import { getFloorPlanUrl, rewriteDefsHref } from '../../utils/unitAssets.js'

import { AssetLoadError }  from '../AssetLoadError/index.js'
import { Button } from '../Button/Button.jsx'
import { ButtonGroup } from '../Button/ButtonGroup.jsx'
import { Skeleton }        from '../Skeleton/index.js'

import styles from './UnitPlanThumb.module.scss'

const svgThumbCache = new Map()
const svgThumbPromiseCache = new Map()

// ── SVG helpers ────────────────────────────────────────────────────────────────

/**
 * Strips XML declaration, DOCTYPE, and script tags from raw SVG text.
 * The SVGs are our own generator output — this is a safety measure, not
 * full sanitisation.
 *
 * @param {string} raw
 * @returns {string}
 */
function cleanSvgText(raw) {
  return raw
    .replace(/<\?xml[^>]*\?>/gi, '')
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .trim()
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * UnitPlanThumb — floor plan thumbnail for a product tile.
 *
 * Renders in a 1:1 square filling the tile width. Three display modes:
 *
 * 1. `planVisible === false`  → placeholder icon (no plan shown per CMS setting)
 * 2. `use3d === true`         → PNG 3D thumb via `<img>` (TODO: key name TBD)
 * 3. default                  → SVG floor plan, fetched + inlined via dangerouslySetInnerHTML
 *                               (required because our SVGs use external `<defs>` references
 *                                which are blocked in `<img>` and `<object>` contexts)
 *
 * Multi-floor products (duplex, maisonette) show a tier toggle in the bottom-right corner.
 *
 * @param {object}  props
 * @param {object}  props.product               - Product data object (uuid, id_cad, tiers, status)
 * @param {string}  props.projectDataUrl        - Base asset URL (from window.bimCadaplusData)
 * @param {string|null} [props.logoSrc]         - Pre-resolved logo (from AppContext) for error states
 * @param {boolean} [props.planVisible=true]    - false → show placeholder (CMS accessibility setting)
 * @param {boolean} [props.use3d=false]         - true → load .png 3D thumb instead of .svg plan
 * @param {string}  [props.className]           - Extra class on root element
 */
export function UnitPlanThumb({ 
  product, 
  projectDataUrl, 
  logoSrc = null, 
  planVisible = true, 
  use3d = false,
  showLevelToggle = true, 
  className 
}) {
  // Build tier list from product.tiers; fall back to single-tier using ref_tier
  const tiers       = product.tiers ?? []
  const tierNums    = tiers.length > 0
    ? [...new Set(tiers.map(t => t.ref_tier))].sort((a, b) => a - b)
    : (product.ref_tier != null ? [product.ref_tier] : [])

  const isMultiFloor = tierNums.length > 1

  const [activeTier, setActiveTier] = useState(tierNums[0] ?? null)
  const [loadStatus, setLoadStatus] = useState('loading') // 'loading' | 'loaded' | 'error'
  const [svgContent, setSvgContent] = useState(null)

  // Reset tier to first when product changes
  useEffect(() => {
    setActiveTier(tierNums[0] ?? null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.uuid])

  // Fetch SVG when URL changes (also reset status)
  const url = (projectDataUrl && activeTier != null)
    ? getFloorPlanUrl(projectDataUrl, product, activeTier, use3d ? '3d' : 'svg')
    : null

  // Reset load status whenever the target URL or mode switches.
  // The SVG fetch effect resets itself, but the 3D <img> path has no fetch effect —
  // this covers that case (and the SVG path harmlessly re-sets to 'loading' too).
  useEffect(() => {
    setLoadStatus('loading')
    setSvgContent(null)
  }, [url, use3d])

  useEffect(() => {
    if (!url || !planVisible || use3d) return

    const cachedSvg = svgThumbCache.get(url)
    if (cachedSvg) {
      setSvgContent(cachedSvg)
      setLoadStatus('loaded')
      return
    }

    setLoadStatus('loading')
    setSvgContent(null)

    let cancelled = false

    let pendingPromise = svgThumbPromiseCache.get(url)

    if (!pendingPromise) {
      pendingPromise = fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          // Reject HTML responses — dev servers (Vite, WP) return 200 + HTML for
          // missing assets instead of 404. Content-Type check is the fast path.
          const ct = res.headers.get('content-type') ?? ''
          if (ct.includes('text/html')) throw new Error('HTML response (asset missing)')
          return res.text()
        })
        .then(text => {
          const cleaned = cleanSvgText(text)
          if (!cleaned.includes('<svg')) throw new Error('Response is not an SVG')
          const withDefs = rewriteDefsHref(cleaned, projectDataUrl)
          svgThumbCache.set(url, withDefs)
          return withDefs
        })
        .finally(() => {
          svgThumbPromiseCache.delete(url)
        })

      svgThumbPromiseCache.set(url, pendingPromise)
    }

    pendingPromise
      .then((svgText) => {
        if (cancelled) return
        setSvgContent(svgText)
        setLoadStatus('loaded')
      })
      .catch(() => {
        if (!cancelled) setLoadStatus('error')
      })

    return () => { cancelled = true }
  }, [url, planVisible, use3d])

  const rootClass = [styles.root, className].filter(Boolean).join(' ')

  // ── Plan hidden by CMS accessibility setting ────────────────────────────────
  if (!planVisible) {
    return (
      <div className={rootClass}>
        <div className={styles.planHidden} aria-hidden="true">
          {/* TODO: replace with dedicated SVG asset for "plan hidden" state */}
          {/* <span className={styles.planHiddenIcon}>⊡</span> */}
          <img
            src={logoSrc}
            alt=""
            aria-hidden="true"
            className={styles.planImage}
          />
        </div>
      </div>
    )
  }

  // ── No URL (missing data) ───────────────────────────────────────────────────
  if (!url) return null

  return (
    <div className={rootClass}>
      {/* ── Loading skeleton ── */}
      {loadStatus === 'loading' && (
        <Skeleton className={styles.skeletonFill} />
      )}

      {/* ── Error state ── */}
      {loadStatus === 'error' && (
        <AssetLoadError
          logoSrc={logoSrc}
          className={styles.errorFill}
        />
      )}

      {/* ── PNG / 3D thumb ──
           Always rendered when use3d=true so the browser fires onLoad/onError.
           Hidden behind skeleton while loading via opacity+position, not display:none
           (display:none suppresses fetch in some environments). */}
      {use3d && (
        <img
          src={url}
          alt=""
          aria-hidden="true"
          className={styles.planImage}
          style={loadStatus !== 'loaded' ? { opacity: 0, position: 'absolute' } : undefined}
          onLoad={() => setLoadStatus('loaded')}
          onError={() => setLoadStatus('error')}
        />
      )}

      {/* ── SVG inline ── */}
      {!use3d && loadStatus === 'loaded' && svgContent && (
        <div
          className={`${styles.planSvg} bim-plan`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: svgContent }}
          aria-hidden="true"
        />
      )}

      {/* ── Multi-floor tier toggle (duplex, maisonette) ── */}
      {isMultiFloor && loadStatus === 'loaded' && showLevelToggle &&(
        <div className={styles.tierToggle}>
          <ButtonGroup
            sliding
            size="xsmall"
            className={styles.toggleGroup}
          >
            {tierNums.map(tier => (
              <Button
                key={tier}
                type="button"
                kind="icon"
                size="xsmall"
                selected={tier === activeTier}
                onClick={e => { e.stopPropagation(); setActiveTier(tier) }}
                aria-label={`Piętro ${tier}`}
                aria-pressed={tier === activeTier}
              >
                {tier}
              </Button>
            ))}
          </ButtonGroup>
        </div>
      )}
    </div>
  )
}
